import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { format, subDays, addDays, isSameDay } from 'date-fns';
import {useAuth} from '../context/AuthContext'
import {useDate} from '../pages/Layout'
import { storageService } from '../service/storage';
import DotGrid from './DotGrid';

/**
 * Componente que muestra un gráfico de barras con los ingresos totales de los últimos 12 meses.
 * @param {Object} props - Propiedades del componente.
 */
const ChartSection = () => {
  const {getVentasPorHora} = useAuth(); 
  const { currentDate,setCurrentDate, setIsLoading } = useDate();
  
  const [hourlyData,setHourlyData] = useState([]);
  const [totalDaily,setTotalDaily] = useState(0);
  const [loading, setLoading] = useState(true); // 👈 Estado de carga
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setIsLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      const date = format(currentDate, 'yyyy-MM-dd')
      const ventasPetition = await getVentasPorHora(date);
      const hourlyData = ventasPetition.data;
      storageService.setGraphHourData(hourlyData);
      console.log("hourlyData");
      console.log(hourlyData);
      setHourlyData(hourlyData);
      const totalDaily = hourlyData.reduce((sum, item) => sum + item.pvp, 0);
      setTotalDaily(totalDaily);
      console.log(totalDaily);
    } catch (err) {
      console.error('Error cargando datos del gráfico:', err);
      setError('No se pudieron cargar los datos');
      setHourlyData([]);
      setTotalDaily(0);
    } finally {
      // Calcular cuánto tiempo ha pasado para el mínimo de 1s
      const elapsed = Date.now() - startTime;
      const minDelay = 1000;
      
      if (elapsed < minDelay) {
        setTimeout(() => {
          setLoading(false);
          setIsLoading(false);
        }, minDelay - elapsed);
      } else {
        setLoading(false);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  if (loading) {
    return React.createElement(
      'section',
      { className: 'chart-section chart-section--loading' },
      React.createElement(
        'div',
        { className: 'chart-loader-overlay' },
        React.createElement(DotGrid, null),
        React.createElement('span', { className: 'chart-loader-text' }, 'Cargando ingresos...')
      )
    );
  }

  if (error) {
    return React.createElement(
      'section',
      { className: 'chart-section chart-section--error' },
      React.createElement(
        'div',
        { className: 'chart-error-overlay' },
        React.createElement(
          'div',
          { className: 'error-icon' },
          React.createElement(
            'svg',
            { width: 32, height: 32, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
            React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
            React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
            React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
          )
        ),
        React.createElement('span', { className: 'error-text' }, error),
        React.createElement(
          'button',
          { 
            className: 'retry-btn',
            onClick: fetchData 
          },
          'Reintentar'
        )
      )
    );
  }

  // 👇 Render normal del gráfico (tu código original)
  return React.createElement(
    'section',
    { className: 'chart-section' },
    React.createElement(
      'div',
      { className: 'chart-header' },
      React.createElement(
        'div',
        { className: 'chart-title-group' },
        React.createElement('h2', null, 'Ingresos por Hora'),
        React.createElement('p', { className: 'chart-subtitle' }, 'Hoy')
      ),
      React.createElement(
        'div',
        { className: 'total-badge' },
        React.createElement('span', { className: 'total-label' }, 'Total del Día'),
        React.createElement('span', { className: 'total-value' }, totalDaily.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })+'€')
      )
    ),
    React.createElement(
      'div',
      { className: 'chart-container' },
      React.createElement(
        ResponsiveContainer,
        { width: '100%', height: 250 },
        React.createElement(
          BarChart,
          {
            data: hourlyData,
            margin: { top: 10, right: 10, left: -20, bottom: 0 },
            barSize: 20
          },
          React.createElement(
            'defs',
            null,
            React.createElement(
              'linearGradient',
              { id: 'colorSales', x1: '0', y1: '0', x2: '0', y2: '1' },
              React.createElement('stop', { offset: '5%', stopColor: '#1397C5', stopOpacity: 0.8 }),
              React.createElement('stop', { offset: '95%', stopColor: '#1397C5', stopOpacity: 0.3 })
            )
          ),
          React.createElement(CartesianGrid, {
            strokeDasharray: '3 3',
            stroke: 'rgba(255,255,255,0.05)',
            vertical: false
          }),
          React.createElement(XAxis, {
            dataKey: 'hora',
            stroke: 'rgba(255,255,255,0.3)',
            fontSize: 11,
            tickLine: false,
            axisLine: false,
            dy: 10,
            interval: 2
          }),
          React.createElement(YAxis, {
            stroke: 'rgba(255,255,255,0.3)',
            fontSize: 11,
            tickLine: false,
            axisLine: false,
            tickFormatter: (value) => `${value}€`
          }),
          React.createElement(Tooltip, {
            cursor: { fill: 'rgba(255,255,255,0.05)' },
            contentStyle: {
              background: '#1a1a1f',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '13px'
            },
            formatter: (value, name, props) => {
              const hour = props?.payload?.hora;
              return [`${value.toLocaleString()}€`, `${hour}`];
            },
            labelFormatter: () => ''
          }),
          React.createElement(Bar, {
            dataKey: 'pvp',
            fill: 'url(#colorSales)',
            stroke: '#1397C5',
            strokeWidth: 1,
            radius: [4, 4, 0, 0],
            animationDuration: 1000
          })
        )
      )
    )
  );
};

export default ChartSection;